import React, { useState } from 'react';

const PasswordGeneratorTool: React.FC<{ slug?: string }> = () => {
  const [length, setLength] = useState(16);
  const [includeUpper, setIncludeUpper] = useState(true);
  const [includeLower, setIncludeLower] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const generate = () => {
    let chars = '';
    if (includeUpper) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeLower) chars += 'abcdefghijklmnopqrstuvwxyz';
    if (includeNumbers) chars += '0123456789';
    if (includeSymbols) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    if (!chars) chars = 'abcdefghijklmnopqrstuvwxyz';
    let p = '';
    const arr = new Uint8Array(length);
    crypto.getRandomValues(arr);
    for (let i = 0; i < length; i++) p += chars[arr[i] % chars.length];
    setPassword(p);
    setCopied(false);
  };

  const copy = async () => {
    if (!password) return;
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Length */}
      <div>
        <style>{`
          .pwd-slider {
            --slider-pct: ${((length - 8) / (32 - 8)) * 100}%;
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 8px;
            border: none;
            border-radius: 9999px;
            cursor: pointer;
            background: linear-gradient(to right, #f97316 var(--slider-pct), #e5e7eb var(--slider-pct));
          }
          .pwd-slider::-webkit-slider-runnable-track {
            height: 8px;
            border: none;
            border-radius: 9999px;
            background: linear-gradient(to right, #f97316 var(--slider-pct), #e5e7eb var(--slider-pct));
          }
          .pwd-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #f97316;
            border: none;
            box-shadow: 0 1px 3px rgba(0,0,0,0.15);
            cursor: pointer;
            margin-top: -6px;
          }
          .pwd-slider::-moz-range-track {
            height: 8px;
            border: none;
            border-radius: 9999px;
            background: linear-gradient(to right, #f97316 var(--slider-pct), #e5e7eb var(--slider-pct));
          }
          .pwd-slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #f97316;
            border: none;
            box-shadow: 0 1px 3px rgba(0,0,0,0.15);
            cursor: pointer;
          }
        `}</style>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Length</label>
          <span className="text-sm font-semibold text-orange-600 tabular-nums">{length}</span>
        </div>
        <input
          type="range"
          min={8}
          max={32}
          value={length}
          onChange={(e) => setLength(Number(e.target.value))}
          className="pwd-slider"
        />
      </div>

      {/* Character options - orange theme for checkboxes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Include</label>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeUpper}
              onChange={(e) => setIncludeUpper(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 accent-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-sm text-gray-700">Uppercase</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeLower}
              onChange={(e) => setIncludeLower(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 accent-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-sm text-gray-700">Lowercase</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeNumbers}
              onChange={(e) => setIncludeNumbers(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 accent-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-sm text-gray-700">Numbers</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeSymbols}
              onChange={(e) => setIncludeSymbols(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 accent-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-sm text-gray-700">Symbols</span>
          </label>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={generate}
          className="px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 shadow-sm hover:shadow transition-all"
        >
          Generate
        </button>
        {password && (
          <button
            type="button"
            onClick={copy}
            className="px-6 py-3 border border-orange-200 rounded-xl font-medium text-orange-600 hover:bg-orange-50 transition-colors"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        )}
      </div>

      {/* Password display - always visible */}
      <div className="p-4 bg-orange-50 rounded-xl border border-orange-100 min-h-[4rem] flex items-center">
        {password ? (
          <p className="font-mono text-sm break-all text-gray-800 select-all">{password}</p>
        ) : (
          <p className="text-sm text-gray-500">Your password will appear here after you click Generate.</p>
        )}
      </div>
    </div>
  );
};

export default PasswordGeneratorTool;
